import { rgba } from 'polished';

import { Breakpoints, CONTENT_MAX_WIDTH } from '../../utils/mediaQueries';
import { units } from '../../utils/spacing';
import { FontSizes, FontWeights } from '../../utils/typography/types';
import { FocusRingVariants } from '../Button/focusRingVariants';
import { ButtonSizes } from '../Button/types';

export const colors = {
	// Palette with nested attributes only works for arrays, not objects unfortunately
	transparent: 'transparent',

	// Solids
	white: '#FFFFFF',
	offWhite: '#FBFBFB',
	gray0: '#F7F7F7',
	gray1: '#E2E2E2',
	gray2: '#C6C6C6',
	gray3: '#AAAAAA',
	gray4: '#8D8D8D',
	gray5: '#717171',
	gray6: '#555555',
	gray7: '#383838',
	gray8: '#1C1C1C',
	black: '#000000',
	offBlack: '#333333',
	black1: '#1A1A2C',
	black2: '#001C38',

	blue1: '#3BBEEC',
	blue2: '#389FE6',
	blue3: '#555BE4',
	blue4: '#61ACE7',
	blue5: '#1B3E8E',
	blue6: '#1C3655',
	blue7: '#1A3E6F',
	linkBlue: '#2275D3',
	darkBlue: '#002152',
	headerBlue: '#176293',
	brown: '#885500',
	gold: '#F8D22D',
	heartRed: '#E80000',
	errorRed: '#E5104C',
	yellow: '#F6DF24',
	blackAlpha5: 'rgba(0, 0, 0, 0.05)',
	blackAlpha10: 'rgba(0, 0, 0, 0.1)',
	blackAlpha15: 'rgba(0, 0, 0, 0.15)',
	blackAlpha20: 'rgba(0, 0, 0, 0.2)',
	blackAlpha60: 'rgba(0, 0, 0, 0.6)',
	blackAlpha90: 'rgba(0, 0, 0, 0.9)',
	whiteAlpha10: 'rgba(255, 255, 255, 0.1)',
	whiteAlpha20: 'rgba(255, 255, 255, 0.2)',
	whiteAlpha40: 'rgba(255, 255, 255, 0.4)',
	whiteAlpha60: 'rgba(255, 255, 255, 0.6)',
	whiteAlpha70: 'rgba(255, 255, 255, 0.7)',
	whiteAlpha80: 'rgba(255, 255, 255, 0.8)',

	graphBlue: '#75B2F0',
	graphOrange: '#F39871',
	graphPink: '#F075C7',
	graphLimeGreen: '#9AF075',
	graphTeal: '#75F0F0',

	// Buttons (prefer functionalColors)
	buttonBlue: '#1A3E6F',
	buttonGreen: '#80C138',
	buttonLime: '#53D477',
	buttonPurple: '#8B5BF4',
	buttonPaypalYellow: '#ffc439',
	buttonFuschia: '#D81B60',

	// Banner
	successGreen: '#28865E',

	// Social
	facebookBlue: '#436BBF',
	googleRed: '#EA4335',

	// Schools
	schoolBlue: '#5a80ee',
	schoolBabyBlue: '#ddecf6',

	// App
	transparentBlackBackground: 'rgba(0, 0, 0, 0.3)',
	transparentBlackBackground10: 'rgba(0, 0, 0, 0.1)',
	transparentWhiteBackground: 'rgba(255, 255, 255, 0.1)',
	// TODO: deduplicate with darkBlue
	midnightBlue: '#002152',
	footerBlue: '#1B2250',

	// Marketing
	jayBlue: '#5C90DC',

	// Zoom App
	blackAlpha80: 'rgba(0, 0, 0, 0.8)',

	// Higher contrast error & success message backgrounds for white text
	errorBackground: '#B3003D',
	successBackground: '#316500',

	// Calm Health
	chLightPrimary: '#0060A9',
	chLightSubText: 'rgba(26, 26, 44, 0.70)',

	// Sleep AI
	sleepAiUnselectedPurple: '#292351',
	sleepAiSelectedPurple: '#423C6A',
	sleepAiBackgroundBlue: '#130B45',
	sleepAiErrorRed: '#FF6B8A',

	// Calm Sleep App
	calmSleepAppBlue: '#2A6DFA',
};

export const gradients = {
	// Gradients
	gradientBrightBlue: 'linear-gradient(135deg, #75F094 0%, #75B2F0 100%)',
	gradientBrightGreen: 'linear-gradient(102.56deg, #9ED73C 7.02%, #45A22A 92.87%)',
	gradientDarkBlue: 'linear-gradient(180deg, #156494 0%, #2f2f7e 100%)',
	gradientBrightOrange: 'linear-gradient(102.56deg, #FFC52B 7.02%, #FF8B3A 92.87%)',
	gradientBrightPurple: 'linear-gradient(102.56deg, #9F83FF 7.02%, #6E58FF 92.87%)',
	gradientBrightRed: 'linear-gradient(102.56deg, #FF3B4C 7.17%, #FF7969 91.63%)',
	gradientCalmBusinessBrand: 'linear-gradient(180deg, transparent 0%, #BCDDF6 100%);',
	gradientCalmBusinessSelfServe: 'linear-gradient(180deg, #E2F6FF 0%, #FDF7F9 100%);',
	gradientCalmBrand: 'linear-gradient(180deg, #60B4E7 0%, #6461E0 100%)',
	gradientCalmPurple: `linear-gradient(225deg, rgba(37, 0, 164, 0.0001) 0%, rgba(27, 0, 118, 0.5) 100%),
      linear-gradient(180deg, #9F83FF 0%, #6E58FF 100%)`,
	gradientCalmBrandDark: 'linear-gradient(180deg, #266893 0%, #28278C 100%)',
	gradientMountainLake: 'linear-gradient(180deg, #005878 0%, #001A40 100%)',
	gradientPink: 'linear-gradient(135deg, #B275F0 0%, #F0758A 100%)',
	gradientPinkToRight: 'linear-gradient(to right, #9191ff, #f075c7)',
	gradientFocus: 'linear-gradient(135deg, #FFC777 0%, #ED6060 100%)',
	gradientYellow: 'linear-gradient(135deg, #F0A875 0%, #F0EC75 100%)',
	gradientGuestPass: 'linear-gradient(4.18deg, #EFAA22 5.76%, #FFDE8F 117.22%)',
	gradientRainbow1: 'linear-gradient(135deg, #F0758A 0%, #F0A875 100%)',
	gradientRainbow2: 'linear-gradient(135deg, #F39871 0%, #F0D975 100%)',
	gradientRainbow3: 'linear-gradient(135deg, #F0EA75 0%, #9AF075 100%)',
	gradientRainbow3b: 'linear-gradient(135deg, #A0EB67 0%, #57E779 100%)',
	gradientRainbow4: 'linear-gradient(135deg, #75F094 0%, #75B2F0 100%)',
	gradientRainbow5: 'linear-gradient(135deg, #75F0F0 0%, #7575F0 100%)',
	gradientRainbow6: 'linear-gradient(135deg, #75B2F0 0%, #B275F0 100%)',
	gradientRainbow6_dark: 'linear-gradient(135deg, #5340CB 0%, #8946A3 100%)',
	gradientRainbow7: 'linear-gradient(135deg, #9191FF 0%, #F075C7 100%)',
	gradientRainbow8: 'linear-gradient(135deg, #B275F0 0%, #F0758A 100%)',
	gradientRainbow9: 'linear-gradient(135deg, #F0758A 0%, #F0A875 100%)',
	gradientRainbow10:
		'linear-gradient(90.06deg, #5c56e6 0.06%, #3abbeb 43.17%, #bce808 72.88%, #ffb800 91.63%, #ff5c00 100.52%)',
	gradientRelax: 'linear-gradient(135deg, #A9EBFF 0%, #5556D7 100%)',
	gradientSleepDark:
		'linear-gradient(45.76deg, rgba(122, 175, 234, 0.56) 0.28%, rgba(121, 59, 235, 0.0001) 61.84%), #3B0E7D',
	gradientSleepLight: 'linear-gradient(135deg, #F29DFF 0%, #8F3AC7 100%)',
	gradientWebApp: 'linear-gradient(to right,#B275F0 0%,#F0758A 100%)',

	// Gradients - Tabs
	gradientTabBlue: 'linear-gradient(180deg, #3A8CBD 0%, #3B399C 100%)',
	gradientTabGray: 'linear-gradient(180deg, #333333 0%, #1F1F1F 100%)',
	gradientTabSleep: 'linear-gradient(180deg, #3C1283 0%, #27065D 100%)',
	gradientTabJasperDark: 'linear-gradient(180deg, #6A539E 0%, #220F4A 100%, #220F4A 100%)',

	gradientSceneBlueGold: 'linear-gradient(180deg, #657CF2 0%, #05226F 100%)',

	gradientHome: 'linear-gradient(268deg, rgba(0, 0, 0, 0) 22%, rgba(0, 0, 0, 0.6) 87%)',
	gradientHomeMobile: 'linear-gradient(268deg, rgba(0, 0, 0, 0.3) 22%, rgba(0, 0, 0, 0.6) 87%)',

	gradientHomeVariant: `
      linear-gradient(45deg, rgba(85, 91, 228, 1) 10%, rgba(85, 91, 228, 0) 60%),
      linear-gradient(to right, rgba(56, 159, 230, 1) 15%, rgba(56, 159, 230, 0) 60%),
      linear-gradient(to bottom, rgba(59, 190, 236, 1) 0%, rgba(59, 190, 236, 0) 25%)
    `,
	gradientHomeVariantMobile: `
      linear-gradient(45deg, rgba(85, 91, 228, .8) 0%, rgba(85, 91, 228, 0) 90%),
      linear-gradient(to right, rgba(56, 159, 230, .8) 0%, rgba(56, 159, 230, 0) 140%),
      linear-gradient(to bottom, rgba(59, 190, 236, .8) 0%, rgba(59, 190, 236, 0) 20%)
    `,
	gradientDisabled: 'linear-gradient(268deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 0%)',

	gradientBreatheBalance: 'linear-gradient(160.4deg, #94E662 0%, #5FE2A5 50%, #54ABF0 100%)',
	// Same as gradientBreatheBalance, but all colors are alpha 10%
	gradientLightGreen:
		'linear-gradient(160.4deg, rgba(148, 230, 98, 0.1) 0%, rgba(95, 226, 165, 0.1) 50%, rgba(84, 171, 240, 0.1) 100%)',

	gradientBreatheEnergize: 'linear-gradient(160.4deg, #E8C072 0%, #FB91CE 50%, #A680F7 100%)',
	// Same as gradientBreatheEnergize, but all colors are alpha 10%
	gradientLightPink:
		'linear-gradient(160.4deg, rgba(232, 192, 114, 0.1) 0%, rgba(251, 145, 206, 0.1) 50%, rgba(166, 128, 247, 0.1) 100%)',

	// Gradients for mood check in
	gradientHappy: 'linear-gradient(144.9deg, #42B4C3 8.37%, #64A9D0 92.91%)',
	gradientExcited: 'linear-gradient(144.9deg, #4EB4C8 8.37%, #679BD8 92.91%)',
	gradientGrateful: 'linear-gradient(144.9deg, #64A4D4 8.37%, #7C8EE7 92.91%)',
	gradientRelaxed: 'linear-gradient(144.9deg, #6D9FD5 8.37%, #8187E8 92.91%)',
	gradientContent: 'linear-gradient(144.9deg, #6B91DF 8.37%, #827EE3 92.91%)',
	gradientTired: 'linear-gradient(144.9deg, #6B91DF 8.37%, #827EE3 92.91%)',
	gradientUnsure: 'linear-gradient(144.9deg, #7F8BEB 8.37%, #A483E0 92.91%)',
	gradientBored: 'linear-gradient(144.9deg, #8487E9 8.37%, #C578B4 92.91%)',
	gradientAnxious: 'linear-gradient(144.9deg, #8E81E1 8.37%, #D67A9D 92.91%)',
	gradientAngry: 'linear-gradient(144.9deg, #A386E7 8.37%, #CF81BF 92.91%)',
	gradientStressed: 'linear-gradient(144.9deg, #BB80CB 8.37%, #F98171 92.91%)',
	gradientSad: 'linear-gradient(144.9deg, #E07996 8.37%, #FD8B5B 92.91%)',

	// A11y
	accessibleBrandGradient: 'linear-gradient(180deg, #2477AA 0%, #6461E0 100%)',

	// Accent Rainbow
	accentRainbow04: 'linear-gradient(135deg, #2E6F3B 0%, #3879AF 100%)',

	// Web app upsell gradients
	upsellIconBackgroundGradient: 'linear-gradient(135deg, #75f0f0 0%, #7575f0 100%)',
	upsellFooterBackgroundGradient:
		'linear-gradient(113.79deg, #f1f5fe 7.89%, #c7def8 33%, #d9ecf6 62.5%, #f1f5fe 100%)',

	// Sleep AI
	sleepAiSelectedGradient: 'radial-gradient(34.7% 59.23% at 50% 100%, #191536 23.56%, #423C6A 100%)',
	sleepAiUnselectedGradient: 'radial-gradient(34.7% 59.23% at 50% 100%, #5D5885 23.56%, #292351 100%)',
};

// 1168 max inner width with 40px padding on either side - we'll use border-box sizing
const functionalPalette = {
	text: colors.black,
	success: colors.buttonGreen,
	error: colors.errorRed,

	// Badges
	badgeBg: colors.white,
	badgeText: rgba(colors.black, 0.8),
	badgeBorder: colors.transparent,

	// Banner
	bannerBg: colors.gold,
	bannerText: colors.gray8,
	bannerTextSuccess: colors.successGreen,
	bannerBgSuccess: rgba(colors.successGreen, 0.1),

	// Buttons
	buttonPrimaryBg: colors.buttonBlue,
	buttonPrimaryText: colors.white,
	buttonPrimaryLoader: colors.white,
	buttonSecondaryBg: colors.white,
	buttonSecondaryText: colors.gray7,
	buttonSecondaryLoader: colors.gray7,
	buttonSecondLoader: colors.gray7,
	buttonDisabledBg: colors.gray2,
	buttonDisabledText: colors.white,

	// Chip Buttons
	chipButtonActiveBg: colors.blue3,
	chipButtonActiveText: colors.white,
	chipButtonInactiveBg: colors.gray0,
	chipButtonInactiveText: colors.blue3,
	chipButtonDisabledBg: colors.gray0,
	chipButtonDisabledText: colors.white,
	chipButtonBorderColorActive: colors.blue7,
	chipButtonBorderColorInactive: colors.gray2,

	// Focus rings
	focusRingDark: colors.buttonBlue,
	focusRingLight: colors.white,

	// Checkboxes
	checkboxSelectedBg: colors.blue3,
	checkboxDeselectedBg: colors.white,
	checkboxSelectedBorder: colors.blue3,
	checkboxDeselectedBorder: colors.gray4,
	strongCheckboxDeselectedBorder: colors.gray5,
	checkboxCheckFillColor: colors.white,
	checkboxCheckStrokeColor: colors.transparent,

	// Inputs
	inputBorderEmpty: colors.gray5,
	inputBorderFull: colors.gray6,
	inputBorderFocus: colors.blue3,
	inputBorderSuccess: colors.gray6,
	inputBorderError: colors.errorRed,
	inputText: colors.gray7,
	inputBgDisabled: colors.gray1,
	inputPlaceholder: colors.gray5,
	inputPlaceholderDisabled: colors.gray4,
	inputLabel: colors.gray5,
	inputLabelError: colors.errorRed,
	inputIcon: colors.gray5,
	inputIconValidationError: colors.errorRed,
	inputIconValidationSuccess: colors.buttonLime,

	// Modals
	modalOverlayBg: rgba(colors.black, 0.7),
	modalBg: colors.white,
	modalBorder: colors.white,
	modalTitleBorder: colors.gray1,

	//MultiSelect
	multiSelectButtonBg: colors.white,
	multiSelectButtonText: colors.gray7,
	multiSelectTooltipBg: colors.black,
	multiSelectTooltipText: colors.white,

	// Radio Groups
	radioIndicatorDeselected: colors.gray1,
	radioIndicatorHover: colors.gray2,
	radioIndicatorSelected: colors.linkBlue,
	radioCardBorderDeselected: colors.gray3,
	radioCardBorderSelected: colors.linkBlue,
	radioCardIndicatorBorder: colors.gray4,
	radioCardIndicatorDeselected: colors.transparent,

	// Text buttons
	textButton: colors.black,

	// Toasts
	toastBg: rgba(colors.black, 0.6),
	toastText: colors.white,
	toastIcon: colors.white,
	toastCardBorderColor: colors.transparent,
	toastCardLeftEdgeColor: colors.linkBlue,

	// Notices
	noticeBgSuccess: colors.buttonLime,
	noticeBgError: colors.heartRed,
	noticeTextSuccess: colors.black,
	noticeTextError: colors.white,

	// Tooltips:
	tooltipBg: colors.black,
	tooltipText: colors.white,

	// Collapsible:
	collapsibleText: colors.black,
	collapsibleIcon: colors.linkBlue,
};

const calmPalette = {
	...colors,
	...gradients,
	...functionalPalette,
};

export type CalmPaletteColor = keyof typeof calmPalette;

export const CalmTheme = {
	name: 'calm',
	breakpoints: Breakpoints,
	fonts: {
		base: 1, // rem values
		small: 0.75,
		fontFamily: 'Figtree',
	},
	palette: calmPalette,
	buttons: {
		minHeight: '48px',
		defaultSize: ButtonSizes.Md,
		borderRadius: '100px',
		primaryHideShadow: false,
		secondaryHideShadow: false,
		shadow: 'none',
		hoverBackground: 'rgba(0, 0, 0, 0.15)',
		hoverShadow: 'none',
		focusRingWidth: '2px',
		focusRingDistance: '-6px',
		focusShadow: 'none',
		primaryFocusRingVariant: FocusRingVariants.Dark,
		secondaryFocusRingVariant: FocusRingVariants.Dark,
		pressedBorderShadow: 'none',
		pressedInnerShadow: 'none',
		pressedTextShadow: 'none',
		chipButtons: {
			borderRadius: '10px',
			padding: '9px 16px',
		},
		iconButtons: {
			borderRadius: '100px',
			shadow: 'none',
			hoverBackground: 'rgba(0, 0, 0, 0.15)',
			hoverShadow: 'none',
			focusRingWidth: '2px',
			focusRingDistance: '-6px',
			focusShadow: 'none',
			pressedBorderShadow: 'none',
			pressedInnerShadow: 'none',
			pressedTextShadow: 'none',
		},
	},
	inputs: {
		borderRadius: '6px',
		borderWidth: '1px',
		borderWidthFocus: '2px',
		borderWidthSuccess: '1px',
		borderWidthError: '1px',
		errorMessageFontSize: FontSizes.sm,
		errorMessageFontWeight: FontWeights.Medium,
		showValidationImage: false,
	},
	checkboxes: {
		textSize: FontSizes.base,
	},
	toasts: {
		shadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
		cardHasBorder: false,
		zIndex: 2147483647,
	},
	tooltips: {
		borderRadius: '6px',
		padding: '0',
	},
	navigation: {
		maxWidth: `${CONTENT_MAX_WIDTH}px`,
		height: 120,
		borderRadius: '4px',
	},
	mobileNavigation: {
		height: 80,
	},
	app: {
		mobileHeaderHeight: '64px',
		headerHeight: '100px',
		mobileBannerHeight: '75px',
		bannerHeight: '50px',
		sidebarWidth: '240px',
	},
	animation: {
		transition: '0.3s ease-out',
	},
	calmHealthStyles: {
		boxShadow: '0px 8px 16px rgb(0 0 0 / 15%)',
		calmLive: {
			chatButtonHeight: '52px',
			chatHeaderHeight: '56px',
			chatButtonBorderRadius: '0px 0px 16px 16px',
			chatHeaderBorderRadius: '16px 16px 0px 0px',
		},
	},
	modal: {
		padding: units(5),
		overlayBg: rgba(colors.black, 0.7),
		zIndex: 999,
		titleSize: FontSizes['2xl'],
		titleAlign: 'center',
		titleTopPadding: units(7),
		titleBottomBorder: '0px',
		borderRadius: units(2),
		closeButtonPadding: units(1.5),
	},
	radioGroup: {
		labelProps: {},
	},
};

const portalPalette = {
	...CalmTheme.palette,
	gray0: '#FAFAFA',
	buttonPrimaryBg: colors.blue3,
	buttonSecondaryText: colors.blue3,
	buttonSecondaryLoader: colors.blue3,
	buttonDisabledBg: colors.gray1,
	buttonDisabledText: colors.gray7,
	focusRingDark: colors.blue3,
	inputBorderFull: colors.gray5,
	inputBorderSuccess: colors.buttonLime,
	checkboxCheckStrokeColor: colors.white,
	radioIndicatorSelected: colors.blue3,
	radioCardBorderSelected: colors.blue3,
	toastBg: colors.white,
	toastText: colors.gray8,
	toastIcon: colors.gray8,
	toastCardLeftEdgeColor: colors.blue3,
	toastCardBorderColor: colors.gray2,
	textButton: colors.blue3,
};

export const PortalTheme = {
	...CalmTheme,
	name: 'portal',
	palette: portalPalette,
	buttons: {
		...CalmTheme.buttons,
		defaultSize: ButtonSizes.Sm,
		secondaryHideShadow: true,
		borderRadius: '4px',
		shadow: 'inset 0 -1px 1px rgba(0, 0, 0, 0.2)',
		hoverBackground: 'none',
		hoverShadow: 'inset 0px -8px 24px 0px rgba(0, 0, 0, 0.16)',
		focusRingWidth: '1px',
		focusRingDistance: '0',
		focusShadow: '0px 0px 8px 0px #555BE4, 0px 0px 8px 0px #FFFFFF',
		pressedBorderShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.2)',
		pressedInnerShadow: 'inset 0px 4px 16px 0px rgba(0, 0, 0, 0.24)',
		pressedTextShadow: '0px -1px 1px rgba(0, 0, 0, 0.48)',
	},
	fonts: {
		...CalmTheme.fonts,
		fontFamily: 'Figtree',
	},
	inputs: {
		...CalmTheme.inputs,
		height: 56,
		borderRadius: '4px',
		borderWidthSuccess: '2px',
		borderWidthError: '2px',
		errorMessageFontSize: FontSizes.sm,
		errorMessageFontWeight: FontWeights.Regular,
	},
	checkboxes: {
		...CalmTheme.checkboxes,
		textSize: FontSizes.sm,
	},
	toasts: {
		...CalmTheme.toasts,
		shadow: 'none',
		cardHasBorder: true,
	},
	modal: {
		...CalmTheme.modal,
		padding: units(3),
		overlayBg: rgba(colors.black, 0.7),
		titleSize: FontSizes.lg,
		titleAlign: 'left',
		titleTopPadding: units(2),
		titleBottomBorder: '1px',
		borderRadius: '0px',
		closeButtonPadding: units(1),
	},
	navigation: {
		...CalmTheme.navigation,
		height: 73,
		maxWidth: `${CONTENT_MAX_WIDTH}px`,
		desktopHeight: 80,
		mobileTabletHeight: 60,
		padding: units(1.5, 3),
	},
	grid: {
		cellWidth: 780,
	},
	layout: {
		errorIndex: 3,
		loginIndex: 2,
		navIndex: 2,
		belowNavIndex: 1,
		borderRadius: '4px',
		mainMaxWidth: '1100px',
	},
	card: {
		innerMaxWidth: '505px',
	},
	radioGroup: {
		...CalmTheme.radioGroup,
		labelProps: {
			...CalmTheme.radioGroup.labelProps,
			size: FontSizes.sm,
			paddingBottom: units(2),
		},
	},
};

export const calmGradients = [
	['#75F094', '#75B2F0'],
	['#75F0F0', '#7575F0'],
	['#75B2F0', '#B275F0'],
	['#9191FF', '#F075C7'],
	['#B275F0', '#F0758A'],
	['#F0758A', '#F0A875'],
	['#F39871', '#F0D975'],
	['#FFC52B', '#FF8B3A'],
	['#A0EB67', '#57E779'],
];

export const d2cColors = {
	transparent: 'transparent',
	black: '#000000',
	white: '#ffffff',
	blue1: '#389FE6',
	blue2: '#555BE4',
	darkBlue1: '#42507D',
	darkBlue2: '#233347',
	heartRed: '#FF5959',
	yellow: '#F6DF24',
	gold: '#F8D22D',
	gray1: '#F7F7F7',
	gray2: '#E2E2E2',
	gray3: '#AAAAAA',
	gray4: '#717171',
	gray5: '#383838',
	gray6: '#555555',

	// Buttons
	buttonBlue: '#4A90E2',

	// Brands
	facebookBlue: '#436BBF',
	googleRed: '#EA4335',
	buttonPaypalYellow: '#ffc439',

	// States
	errorRed: '#D12C2C',
	successGreen: '#28865E',
};

export const d2cFunctionalPalette = {
	text: d2cColors.gray5,
	success: d2cColors.successGreen,
	error: d2cColors.errorRed,

	// Buttons
	buttonPrimaryBg: d2cColors.blue2,
	buttonPrimaryText: d2cColors.white,
	buttonDisabledBg: d2cColors.gray3,
	buttonDisabledText: d2cColors.white,

	// Focus rings
	focusRingDark: d2cColors.blue1,
	focusRingLight: d2cColors.white,

	// Modals
	modalOverlayBg: rgba(d2cColors.black, 0.7),
	modalBg: d2cColors.white,
	modalBorder: d2cColors.white,

	// Checkboxes
	checkboxSelectedBg: d2cColors.blue2,
	checkboxDeselectedBg: d2cColors.white,
	checkboxSelectedBorder: d2cColors.blue2,
	checkboxDeselectedBorder: d2cColors.gray3,
	checkboxCheckFillColor: d2cColors.white,
	checkboxCheckStrokeColor: d2cColors.transparent,

	// Inputs
	inputBorderEmpty: d2cColors.gray3,
	inputBorderFull: d2cColors.gray6,
	inputBorderFocus: d2cColors.blue2,
	inputBorderError: d2cColors.errorRed,
	inputText: d2cColors.gray5,
	inputBgDisabled: d2cColors.gray1,
	inputPlaceholder: d2cColors.gray4,
	inputPlaceholderDisabled: d2cColors.gray4,
	inputLabel: d2cColors.gray4,
	inputLabelError: d2cColors.errorRed,

	// Text buttons
	textButton: d2cColors.black,
};

const d2cPalette = {
	...calmPalette,
	...d2cFunctionalPalette,
	...d2cColors,
	...gradients,
};

export const D2CTheme = {
	...CalmTheme,
	name: 'd2c',
	palette: d2cPalette,
};

export type PaletteColor = keyof typeof portalPalette | keyof typeof d2cPalette;

export const sleepColors = {
	...colors,
	blue7: '#2A6DFA',

	buttonBlue: '#2A6DFA',
};

const sleepFunctionalPalette = {
	...functionalPalette,
	buttonPrimaryBg: sleepColors.buttonBlue,
};

const sleepPalette = {
	...calmPalette,
	...sleepFunctionalPalette,
	...sleepColors,
	...gradients,
};

export const SleepTheme = {
	...CalmTheme,
	name: 'sleep',
	palette: sleepPalette,
};
